import {
  t,
  type MarketplaceListing,
  RESOURCE_INFO,
  ResourceTier,
  lookupConsumable,
  lookupWeapon,
  lookupShield,
  lookupEngine,
  parseConsumableItemId,
  itemLabel,
  suggestedMarketUnitPrice,
  sellPriceForResource,
  sellPriceForEquipment,
  MARKETPLACE_MIN_PRICE,
  MARKETPLACE_MAX_PRICE,
  MARKETPLACE_MAX_LISTINGS_PER_PILOT,
  MARKETPLACE_LISTING_TTL_MS,
} from '@viral-space/shared';

const COLORS = {
  primary: '#00ff41',
  secondary: '#ffb000',
  danger: '#ff4444',
  bg: 'rgba(0,8,0,0.94)',
  panelBg: 'rgba(0,12,0,0.8)',
  dim: 'rgba(0,255,65,0.3)',
  dimText: 'rgba(0,255,65,0.55)',
  rowHover: 'rgba(0,255,65,0.08)',
} as const;

const FONT = "'Share Tech Mono', monospace";
const GLOW = `0 0 6px ${COLORS.primary}, 0 0 12px rgba(0,255,65,0.3)`;

type TabId = 'browse' | 'mine' | 'sell';

export interface MarketplaceCallbacks {
  /** Pull a fresh snapshot from the server. */
  onRefresh: (scope: 'all' | 'mine') => void;
  onCreate: (itemId: string, quantity: number, pricePerUnit: number) => void;
  onCancel: (listingId: string) => void;
  onBuy: (listingId: string) => void;
}

interface InventoryView {
  items: Array<{ itemId: string; quantity: number }>;
}

/**
 * Inline marketplace view. The host (currently the station UI's
 * MARKETPLACE tab) calls {@link mount} with a container element when
 * the tab becomes visible and {@link unmount} when it switches away.
 *
 * Three sub-tabs:
 *   - BROWSE: every listing currently up for sale by any pilot.
 *   - MINE  : only listings owned by the local pilot, with CANCEL.
 *   - SELL  : pick an item from cargo, set quantity + price/unit, post.
 *
 * Equipment listings are deferred for now — the server's adapter only
 * implements escrow/transfer for resources and consumables in the MVP.
 *
 * The view re-renders on every {@link refresh} call; the host is
 * expected to invoke that whenever a `MarketplaceListings` /
 * `MarketplaceResult` / `MarketplaceSold` message lands so prices and
 * stock counts stay live without manual refresh actions from the user.
 */
export class MarketplacePanel {
  private root!: HTMLElement;
  private tabBar!: HTMLElement;
  private tabContent!: HTMLElement;
  private statusEl!: HTMLElement;
  private tabButtons = new Map<TabId, HTMLElement>();
  private activeTab: TabId = 'browse';
  private mountedIn: HTMLElement | null = null;
  private localKey: string | null = null;

  /** Live state accessors. */
  getListings: ((scope: 'all' | 'mine') => MarketplaceListing[]) | null = null;
  getServerTime: (() => number) | null = null;
  getInventory: (() => InventoryView) | null = null;
  getCredits: (() => number) | null = null;
  callbacks: MarketplaceCallbacks | null = null;

  constructor() {
    this.build();
  }

  setLocalKey(key: string | null): void {
    this.localKey = key ? key.toLowerCase() : null;
  }

  /**
   * Append the view into a host container and trigger a fresh data
   * pull. Idempotent — calling `mount` twice with the same container
   * just refreshes; switching containers re-parents the root.
   */
  mount(container: HTMLElement): void {
    if (this.mountedIn === container && container.contains(this.root)) {
      this.refresh();
      return;
    }
    container.appendChild(this.root);
    this.mountedIn = container;
    this.callbacks?.onRefresh('all');
    this.callbacks?.onRefresh('mine');
    this.refresh();
  }

  /** Detach the view from its current host without destroying it. */
  unmount(): void {
    if (this.mountedIn && this.root.parentElement === this.mountedIn) {
      this.mountedIn.removeChild(this.root);
    }
    this.mountedIn = null;
    this.setStatus('', true);
  }

  isMounted(): boolean {
    return this.mountedIn !== null && this.mountedIn.contains(this.root);
  }

  refresh(): void {
    if (!this.isMounted()) return;
    this.renderActiveTab();
  }

  /** Show transient feedback (e.g. created/bought/failed reason). */
  setStatus(text: string, ok: boolean): void {
    this.statusEl.textContent = text;
    this.statusEl.style.color = ok ? COLORS.primary : COLORS.danger;
    this.statusEl.style.textShadow = ok ? GLOW : 'none';
    this.statusEl.style.display = text ? 'block' : 'none';
  }

  private el(
    tag: string,
    styles: Partial<CSSStyleDeclaration>,
    text?: string,
  ): HTMLElement {
    const e = document.createElement(tag);
    Object.assign(e.style, styles);
    if (text !== undefined) e.textContent = text;
    return e;
  }

  private build(): void {
    this.root = this.el('div', {
      flex: '1',
      display: 'flex',
      flexDirection: 'column',
      gap: '8px',
      fontFamily: FONT,
      color: COLORS.primary,
      letterSpacing: '1px',
      textTransform: 'uppercase',
      minHeight: '0',
      overflow: 'hidden',
    });

    this.tabBar = this.el('div', {
      display: 'flex',
      borderBottom: `1px solid ${COLORS.dim}`,
      flexShrink: '0',
    });
    this.root.appendChild(this.tabBar);

    const tabs: Array<{ id: TabId; key: string }> = [
      { id: 'browse', key: 'marketplace.tabBrowse' },
      { id: 'mine', key: 'marketplace.tabMine' },
      { id: 'sell', key: 'marketplace.tabSell' },
    ];
    for (const tab of tabs) {
      const btn = this.el('button', {
        fontFamily: FONT,
        fontSize: '12px',
        padding: '8px 16px',
        background: 'transparent',
        border: 'none',
        borderBottom: `2px solid ${COLORS.dim}`,
        color: COLORS.dimText,
        cursor: 'pointer',
        letterSpacing: '2px',
        flex: '1',
        transition: 'all 0.15s',
      }, t(tab.key));
      btn.addEventListener('click', () => this.switchTab(tab.id));
      this.tabBar.appendChild(btn);
      this.tabButtons.set(tab.id, btn);
    }

    this.statusEl = this.el('div', {
      display: 'none',
      fontSize: '11px',
      letterSpacing: '1px',
      padding: '4px 0',
      flexShrink: '0',
    });
    this.root.appendChild(this.statusEl);

    this.tabContent = this.el('div', {
      flex: '1',
      display: 'flex',
      flexDirection: 'column',
      gap: '6px',
      overflowY: 'auto',
      paddingRight: '4px',
      minHeight: '0',
    });
    this.root.appendChild(this.tabContent);
  }

  private switchTab(id: TabId): void {
    this.activeTab = id;
    if (id === 'browse') this.callbacks?.onRefresh('all');
    if (id === 'mine') this.callbacks?.onRefresh('mine');
    this.renderActiveTab();
  }

  private renderActiveTab(): void {
    for (const [id, btn] of this.tabButtons) {
      const active = id === this.activeTab;
      btn.style.color = active ? COLORS.primary : COLORS.dimText;
      btn.style.textShadow = active ? GLOW : 'none';
      btn.style.borderBottomColor = active ? COLORS.primary : COLORS.dim;
    }
    this.tabContent.innerHTML = '';
    if (this.activeTab === 'browse') {
      this.renderBrowse();
    } else if (this.activeTab === 'mine') {
      this.renderMine();
    } else {
      this.renderSell();
    }
  }

  /* ───── Browse ───── */

  private renderBrowse(): void {
    const listings = this.getListings?.('all') ?? [];
    if (listings.length === 0) {
      this.tabContent.appendChild(this.buildEmpty(t('marketplace.empty')));
      return;
    }
    listings.sort((a, b) => a.pricePerUnit - b.pricePerUnit);
    const credits = this.getCredits?.() ?? 0;
    for (const listing of listings) {
      this.tabContent.appendChild(this.buildBrowseRow(listing, credits));
    }
  }

  private buildBrowseRow(listing: MarketplaceListing, credits: number): HTMLElement {
    const row = this.el('div', {
      display: 'flex',
      flexDirection: 'column',
      gap: '4px',
      padding: '8px 10px',
      border: `1px solid ${COLORS.dim}`,
      background: COLORS.panelBg,
    });
    const head = this.el('div', {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'baseline',
      gap: '8px',
    });
    head.appendChild(
      this.el('div', {
        fontSize: '12px',
        textShadow: GLOW,
        letterSpacing: '1px',
        flex: '1',
      }, `${this.labelFor(listing.itemId)} x${listing.quantity}`),
    );
    head.appendChild(
      this.el('div', {
        color: COLORS.secondary,
        fontSize: '12px',
      }, t('marketplace.totalPrice', { n: listing.totalPrice.toLocaleString() })),
    );
    row.appendChild(head);

    const meta = this.el('div', {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      gap: '8px',
      fontSize: '10px',
      color: COLORS.dim,
      flexWrap: 'wrap',
    });
    meta.appendChild(
      this.el('span', {}, t('marketplace.seller', { name: listing.sellerName })),
    );
    meta.appendChild(
      this.el('span', {}, t('marketplace.unitPrice', { n: listing.pricePerUnit.toLocaleString() })),
    );
    meta.appendChild(
      this.el('span', {}, this.formatExpires(listing.expiresAt)),
    );
    row.appendChild(meta);

    const actions = this.el('div', {
      display: 'flex',
      justifyContent: 'flex-end',
      gap: '6px',
      marginTop: '4px',
    });
    const isOwn = !!this.localKey && listing.sellerKey.toLowerCase() === this.localKey;
    if (isOwn) {
      actions.appendChild(
        this.el('span', {
          fontSize: '10px',
          color: COLORS.dim,
          alignSelf: 'center',
        }, t('marketplace.yourListing')),
      );
      actions.appendChild(
        this.makeBtn(t('marketplace.cancel'), () => {
          this.callbacks?.onCancel(listing.id);
        }, COLORS.danger, true),
      );
    } else {
      const canAfford = credits >= listing.totalPrice;
      const buyBtn = this.makeBtn(t('marketplace.buy'), () => {
        if (!canAfford) return;
        this.callbacks?.onBuy(listing.id);
      }, canAfford ? COLORS.primary : COLORS.dim, true);
      if (!canAfford) buyBtn.style.cursor = 'not-allowed';
      actions.appendChild(buyBtn);
    }
    row.appendChild(actions);
    return row;
  }

  /* ───── Mine ───── */

  private renderMine(): void {
    const listings = this.getListings?.('mine') ?? [];
    const header = this.el('div', {
      fontSize: '10px',
      color: COLORS.dim,
      letterSpacing: '2px',
      flexShrink: '0',
    }, t('marketplace.activeCount', {
      n: listings.length,
      cap: MARKETPLACE_MAX_LISTINGS_PER_PILOT,
    }));
    this.tabContent.appendChild(header);
    if (listings.length === 0) {
      this.tabContent.appendChild(this.buildEmpty(t('marketplace.mineEmpty')));
      return;
    }
    for (const listing of listings) {
      this.tabContent.appendChild(this.buildMineRow(listing));
    }
  }

  private buildMineRow(listing: MarketplaceListing): HTMLElement {
    const row = this.el('div', {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      gap: '8px',
      padding: '8px 10px',
      border: `1px solid ${COLORS.dim}`,
      background: COLORS.panelBg,
    });
    const text = this.el('div', { display: 'flex', flexDirection: 'column', flex: '1' });
    text.appendChild(
      this.el('div', {
        fontSize: '12px',
        textShadow: GLOW,
      }, `${this.labelFor(listing.itemId)} x${listing.quantity}`),
    );
    text.appendChild(
      this.el('div', {
        fontSize: '10px',
        color: COLORS.dim,
      }, `${t('marketplace.unitPrice', { n: listing.pricePerUnit.toLocaleString() })} · ${t('marketplace.totalPrice', { n: listing.totalPrice.toLocaleString() })} · ${this.formatExpires(listing.expiresAt)}`),
    );
    row.appendChild(text);
    row.appendChild(
      this.makeBtn(t('marketplace.cancel'), () => {
        this.callbacks?.onCancel(listing.id);
      }, COLORS.danger, true),
    );
    return row;
  }

  /* ───── Sell ───── */

  private renderSell(): void {
    const inv = this.getInventory?.() ?? { items: [] };
    type SellEntry = {
      itemId: string;
      label: string;
      max: number;
      baseValue: number;
      sellable: boolean;
    };
    const entries: SellEntry[] = [];
    for (const slot of inv.items) {
      if (slot.quantity <= 0) continue;
      const meta = this.metaFor(slot.itemId);
      entries.push({
        itemId: slot.itemId,
        label: meta.label,
        max: slot.quantity,
        baseValue: meta.baseValue,
        sellable: meta.sellable,
      });
    }
    entries.sort((a, b) => a.label.localeCompare(b.label));

    if (entries.length === 0) {
      this.tabContent.appendChild(this.buildEmpty(t('marketplace.sellEmpty')));
      return;
    }

    const blurb = this.el('div', {
      fontSize: '10px',
      color: COLORS.dim,
      textTransform: 'none',
      lineHeight: '1.4',
      padding: '4px 0',
      flexShrink: '0',
    }, t('marketplace.sellHelp', {
      days: Math.round(MARKETPLACE_LISTING_TTL_MS / (24 * 60 * 60 * 1000)),
    }));
    this.tabContent.appendChild(blurb);

    let selected: SellEntry | null = entries.find((e) => e.sellable) ?? entries[0];
    let qty = 1;
    let unitPrice = selected ? suggestedMarketUnitPrice(selected.baseValue) : MARKETPLACE_MIN_PRICE;

    const list = this.el('div', {
      display: 'flex',
      flexDirection: 'column',
      gap: '4px',
      maxHeight: '38vh',
      overflowY: 'auto',
      paddingRight: '4px',
      borderTop: `1px solid ${COLORS.dim}`,
      borderBottom: `1px solid ${COLORS.dim}`,
      padding: '6px 0',
    });
    const rowEls: HTMLElement[] = [];

    const updateForm = () => {
      if (!selected) return;
      qtyInput.max = String(selected.max);
      if (qty > selected.max) qty = selected.max;
      qtyInput.value = String(qty);
      priceInput.value = String(unitPrice);
      totalEl.textContent = (qty * unitPrice).toLocaleString();
      submitBtn.style.opacity = selected.sellable ? '1' : '0.4';
      submitBtn.style.cursor = selected.sellable ? 'pointer' : 'not-allowed';
      const idx = entries.indexOf(selected);
      for (let i = 0; i < rowEls.length; i++) {
        rowEls[i].style.background = i === idx ? COLORS.rowHover : 'transparent';
        rowEls[i].style.color = i === idx ? COLORS.primary : COLORS.dimText;
      }
      hintEl.textContent = selected.sellable
        ? t('marketplace.suggested', { n: suggestedMarketUnitPrice(selected.baseValue).toLocaleString() })
        : t('marketplace.notSellable');
      hintEl.style.color = selected.sellable ? COLORS.dim : COLORS.danger;
    };

    for (let i = 0; i < entries.length; i++) {
      const entry = entries[i];
      const row = this.el('div', {
        display: 'flex',
        justifyContent: 'space-between',
        padding: '4px 8px',
        cursor: entry.sellable ? 'pointer' : 'not-allowed',
        fontSize: '11px',
        color: entry.sellable ? COLORS.dimText : COLORS.dim,
        border: `1px solid ${COLORS.dim}`,
        opacity: entry.sellable ? '1' : '0.55',
      });
      row.appendChild(this.el('span', { textTransform: 'none' }, entry.label));
      row.appendChild(this.el('span', { color: COLORS.secondary }, `x${entry.max}`));
      row.addEventListener('click', () => {
        if (!entry.sellable) return;
        selected = entry;
        unitPrice = suggestedMarketUnitPrice(entry.baseValue);
        qty = 1;
        updateForm();
      });
      list.appendChild(row);
      rowEls.push(row);
    }
    this.tabContent.appendChild(list);

    const formGrid = this.el('div', {
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: '8px 12px',
      marginTop: '8px',
      flexShrink: '0',
    });
    const qtyBox = this.el('div', { display: 'flex', flexDirection: 'column', gap: '2px' });
    qtyBox.appendChild(this.el('span', { fontSize: '10px', color: COLORS.dim }, t('marketplace.qtyLabel')));
    const qtyInput = document.createElement('input');
    qtyInput.type = 'number';
    qtyInput.min = '1';
    qtyInput.value = '1';
    Object.assign(qtyInput.style, {
      fontFamily: FONT,
      fontSize: '12px',
      padding: '4px 6px',
      background: 'rgba(0,8,0,0.85)',
      border: `1px solid ${COLORS.dim}`,
      color: COLORS.primary,
      outline: 'none',
    });
    qtyInput.addEventListener('keydown', (e) => e.stopPropagation());
    qtyInput.addEventListener('input', () => {
      qty = Math.max(1, Number(qtyInput.value) | 0);
      if (selected && qty > selected.max) qty = selected.max;
      qtyInput.value = String(qty);
      totalEl.textContent = (qty * unitPrice).toLocaleString();
    });
    qtyBox.appendChild(qtyInput);
    formGrid.appendChild(qtyBox);

    const priceBox = this.el('div', { display: 'flex', flexDirection: 'column', gap: '2px' });
    priceBox.appendChild(this.el('span', { fontSize: '10px', color: COLORS.dim }, t('marketplace.unitPriceLabel')));
    const priceInput = document.createElement('input');
    priceInput.type = 'number';
    priceInput.min = String(MARKETPLACE_MIN_PRICE);
    priceInput.max = String(MARKETPLACE_MAX_PRICE);
    priceInput.value = String(unitPrice);
    Object.assign(priceInput.style, {
      fontFamily: FONT,
      fontSize: '12px',
      padding: '4px 6px',
      background: 'rgba(0,8,0,0.85)',
      border: `1px solid ${COLORS.dim}`,
      color: COLORS.primary,
      outline: 'none',
    });
    priceInput.addEventListener('keydown', (e) => e.stopPropagation());
    priceInput.addEventListener('input', () => {
      const n = Math.max(MARKETPLACE_MIN_PRICE, Math.min(MARKETPLACE_MAX_PRICE, Number(priceInput.value) | 0));
      unitPrice = n;
      priceInput.value = String(n);
      totalEl.textContent = (qty * unitPrice).toLocaleString();
    });
    priceBox.appendChild(priceInput);
    formGrid.appendChild(priceBox);

    this.tabContent.appendChild(formGrid);

    const hintEl = this.el('div', {
      fontSize: '10px',
      color: COLORS.dim,
      textTransform: 'none',
      marginTop: '4px',
      flexShrink: '0',
    }, '');
    this.tabContent.appendChild(hintEl);

    const totalRow = this.el('div', {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginTop: '8px',
      flexShrink: '0',
    });
    totalRow.appendChild(this.el('span', { fontSize: '11px' }, t('marketplace.totalLabel')));
    const totalEl = this.el('span', {
      fontSize: '13px',
      color: COLORS.secondary,
      textShadow: GLOW,
    }, '0');
    totalRow.appendChild(totalEl);
    this.tabContent.appendChild(totalRow);

    const submitBtn = this.makeBtn(t('marketplace.list'), () => {
      if (!selected || !selected.sellable) return;
      const finalQty = Math.max(1, Math.min(selected.max, qty));
      const finalPrice = Math.max(
        MARKETPLACE_MIN_PRICE,
        Math.min(MARKETPLACE_MAX_PRICE, unitPrice),
      );
      this.callbacks?.onCreate(selected.itemId, finalQty, finalPrice);
    }, COLORS.primary);
    submitBtn.style.alignSelf = 'flex-end';
    submitBtn.style.marginTop = '8px';
    submitBtn.style.flexShrink = '0';
    this.tabContent.appendChild(submitBtn);

    updateForm();
  }

  private buildEmpty(text: string): HTMLElement {
    return this.el('div', {
      color: COLORS.dim,
      fontSize: '11px',
      textAlign: 'center',
      padding: '40px 8px',
      textTransform: 'none',
      lineHeight: '1.4',
    }, text);
  }

  private formatExpires(expiresAt: number): string {
    const serverNow = this.getServerTime?.() || Date.now();
    const remaining = Math.max(0, expiresAt - serverNow);
    if (remaining === 0) return t('marketplace.expired');
    const days = Math.floor(remaining / (24 * 60 * 60 * 1000));
    if (days >= 1) return t('marketplace.expiresDays', { n: days });
    const hours = Math.floor(remaining / (60 * 60 * 1000));
    if (hours >= 1) return t('marketplace.expiresHours', { n: hours });
    const mins = Math.max(1, Math.floor(remaining / (60 * 1000)));
    return t('marketplace.expiresMinutes', { n: mins });
  }

  private metaFor(itemId: string): { label: string; baseValue: number; sellable: boolean } {
    const resourceMatch = itemId.match(/^resource_(\d+)$/);
    if (resourceMatch) {
      const tier = parseInt(resourceMatch[1], 10) as ResourceTier;
      const info = RESOURCE_INFO[tier];
      if (info) {
        return {
          label: info.name,
          baseValue: sellPriceForResource(tier),
          sellable: true,
        };
      }
    }
    const consumableId = parseConsumableItemId(itemId);
    if (consumableId) {
      const def = lookupConsumable(consumableId);
      if (def) {
        return {
          label: itemLabel('consumable', consumableId, def.name),
          baseValue: Math.max(1, def.creditCost ?? 1),
          sellable: true,
        };
      }
    }
    const w = lookupWeapon(itemId);
    if (w) {
      return {
        label: itemLabel('weapon', itemId, w.name),
        baseValue: sellPriceForEquipment(w.creditCost ?? 0),
        sellable: false,
      };
    }
    const s = lookupShield(itemId);
    if (s) {
      return {
        label: itemLabel('shield', itemId, s.name),
        baseValue: sellPriceForEquipment(s.creditCost ?? 0),
        sellable: false,
      };
    }
    const e = lookupEngine(itemId);
    if (e) {
      return {
        label: itemLabel('engine', itemId, e.name),
        baseValue: sellPriceForEquipment(e.creditCost ?? 0),
        sellable: false,
      };
    }
    return { label: itemId, baseValue: 1, sellable: false };
  }

  private labelFor(itemId: string): string {
    return this.metaFor(itemId).label;
  }

  private makeBtn(
    label: string,
    onClick: () => void,
    color: string,
    compact = false,
  ): HTMLElement {
    const btn = this.el('button', {
      fontFamily: FONT,
      fontSize: compact ? '10px' : '11px',
      padding: compact ? '4px 8px' : '5px 12px',
      background: 'transparent',
      border: `1px solid ${color}`,
      color,
      cursor: 'pointer',
      letterSpacing: '2px',
      transition: 'all 0.15s',
    }, label);
    btn.addEventListener('mouseenter', () => {
      btn.style.background = color;
      btn.style.color = '#000';
    });
    btn.addEventListener('mouseleave', () => {
      btn.style.background = 'transparent';
      btn.style.color = color;
    });
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      onClick();
    });
    return btn;
  }
}
